import { catchError, switchMap, filter, takeUntil, mergeMap, delay, throttleTime, mapTo } from 'rxjs/operators'
import { ajaxCatch } from 'rxOperators/ajaxCatch'
import { filterRequest } from 'rxOperators/filterRequestOfType'
import * as reduce from 'lodash/reduce'
import * as map from 'lodash/map'
import * as isNumber from 'lodash/isNumber'
import * as moment from 'moment'
import { TestScheduler } from 'rxjs/testing'
import { Epic, EpicRefined } from 'interfaces/middleware'
import {
  AUTHENTICATE_LINKAGE,
  authenticateLinkageActionCreator,
  AuthenticateLinkageRequestPayload,
  CANCEL_MFA,
  cancelMfaActionCreator,
  createFakeProviderAccountActionCreator,
  GET_DETAILED_PROVIDER,
  GET_MIXED_ACCOUNTS,
  GET_PROVIDERS,
  getDetailedProviderActionCreator,
  getMixedAccountsActionCreator,
  GetMixedAccountsSuccessPayload,
  getProvidersActionCreator,
  killLinkageSessionActionCreator,
  PERFORM_MIXED_ACCOUNT_OPERATION,
  performMixedAccountOperationActionCreator,
  POLL_PROVIDER_ACCOUNT,
  pollProviderAccountActionCreator,
  SET_MANUAL_BALANCE_ACCOUNT,
  setManualBalanceAccountActionCreator,
  START_LINKAGE_SESSION,
  startLinkageSessionActionCreator,
  SEARCH_PROVIDERS,
  searchProvidersActionCreator,
  CLEAR_PROVIDERS_SEARCH,
  CLEAR_RECENTLY_ADDED,
  clearRecentlyAddedActionCreator,
  FetchMissingAccountsAction,
} from 'actions/linkageActions'
import {
  CANCEL_PROVIDER_ACCOUNT_MFA_URL,
  CREATE_PROVIDER_ACCOUNT_URL,
  DELETE_MANUAL_BALANCE_ACCOUNT_URL,
  DELETE_PROVIDER_ACCOUNT_URL,
  DELETE_PROVIDER_BALANCE_ACCOUNT_URL,
  GET_PROVIDER_ACCOUNT_STATE_URL,
  GET_PROVIDER_ACCOUNTS_ALL_URL,
  GET_PROVIDER_DETAILS_URL,
  GET_PROVIDERS_URL,
  GetProvidersApiPayload,
  LINK_PROVIDER_BALANCE_ACCOUNT_URL,
  SET_PROVIDER_ACCOUNT_MFA_URL,
  UNLINK_MANUAL_BALANCE_ACCOUNT_URL,
  UNLINK_PROVIDER_BALANCE_ACCOUNT_URL,
  UPDATE_PROVIDER_ACCOUNT_URL,
  FETCH_MISSING_ACCOUNTS_URL,
} from 'interfaces/ajax'
import { getResponseFormErrors, processError } from 'helpers/ajaxHelpers'
import {
  LinkageForm,
  LinkageSession,
  ManualBalanceAccount,
  MixedCompositeId,
  ProcessedRawProviderAccount,
  Provider,
  ProviderAccount,
  ProviderAccountState,
  ProviderBalanceAccount,
  ProviderBalanceAccountCollection,
  ProviderCollection,
  ProviderId,
  ProviderSearchOption,
  RawManualBalanceAccount,
  RawMixedProviderAccount,
  RawProviderAccount,
} from 'interfaces/linkage'
import { Observable, merge, empty, of, from, concat, EMPTY } from 'rxjs'
import { concatCollections, createEmptyCollection, getItemsFromCollection, normalize } from 'helpers/normalizeHelpers'
import { metaSelector } from 'selectors/globalSelectors'
import { captureWarning } from 'helpers/errorHelpers'
import { MetaData } from 'interfaces/meta'
import { Action } from 'actions'
import {
  anyMixedLinkageEntitySelector,
  createProviderAccountSelector,
  createProviderAccountStateSelector,
  createProviderSelector,
  linkageFormSelector,
  linkageSessionSelector,
  providerAccountBySessionSelector,
  providerAccountStateBySessionSelector,
  providerBySessionSelector,
  providerAccountStatesSelector,
} from 'selectors/linkageSelectors'
import { encryptCredentials } from 'helpers/cryptoHelpers'
import { AjaxResponse } from 'interfaces'
import { isServerFailedError, NOT_FOUND_STATUS_CODE, VALIDATION_ERROR_STATUS_CODE } from 'constants/ajax'
import { Store } from 'redux'
import { getMangledFieldNames } from 'helpers/formHelpers'
import { State } from 'interfaces/state'
import { FORM_LINKAGE_AUTHENTICATION, FORM_MANUAL_ACCOUNT, FORM_PROVIDER_SEARCH, FORM_PROVIDER_SEARCH_FIELD } from 'constants/forms'
import { blur, stopSubmit } from 'redux-form'
import { AjaxError } from 'rxjs/ajax'
import { isSuccessAction } from 'helpers/actionsHelpers'
import { combineEpics } from 'redux-observable'
import { MixedAccountOperationApiResponse } from 'interfaces/ajax/payload'
import {
  createCompositeId,
  extractLinkageEntityFromCid,
  getAccountKind,
  isFakeProviderAccountCid,
  isInProgressState,
  isMFAState,
  isProvider,
  isProviderAccount,
  processApiActions,
  getProviderAccountAnchorId,
} from 'helpers/linkageHelpers'
import { activateAnchorActionCreator, pushActionCreator } from 'actions/navigationActions'
import { Anchor, AnchorActivationOptions } from 'interfaces/navigation'
import { addAccomplishmentActionCreator } from 'actions/bountyActions'
import { Accomplishmentable } from 'interfaces/bounty'
import { isAccountOperationSucceed, isPollingProviderAccountReady } from 'helpers/accountHelpers'
import { markDataStaleActionCreator } from 'actions/globalActions'
import { LINKED_ACCOUNTS_URL } from 'tangle-shared'
import { INSIGHT_CTA_CLICK } from 'actions/insightsActions'
import { modifyFloatingPaneActionCreator, killAllFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { formSubmitted } from 'rxOperators/formsLegacy'
import { mapSafe, mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { catchAjaxSafe } from 'rxOperators/catchSafe'
import { AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'
// TODO: Performance
// TODO: Analytics

/**
 * Terminology:
 * `PROVIDER` is one who brings an ability to connect user bank accounts.
 * `PROVIDER_ACCOUNT` is a particular user account which user tries to log in.
 * `PROVIDER_BALANCE_ACCOUNT` is a small account which lives inside a `PROVIDER_ACCOUNT`. Many `PROVIDER_BALANCE_ACCOUNT`s may be linked to single `PROVIDER_ACCOUNT`.
 *
 * The onboarding account linkage workflow is the following:
 * 1. We request a list of popular providers hitting `/accounts/providers/` and display it on the UI.
 * 2. A user clicks on a PROVIDER badge (triggering `START_LINKAGE_SESSION` action).
 *    We request general provider description hitting `/accounts/providers/:id/`. After that we got `logo`, `name`, `login_form` fields.
 * 3. The user submits login information (triggering `AUTHENTICATE_LINKAGE` action).
 *    We hit `/accounts/providers/create/` and create an account-provider linkage. Backend responds to us with `verifying` PROVIDER_ACCOUNT status.
 *    Then we start polling actual PROVIDER_ACCOUNT status. A set of statuses is available here:
 *      * `failed_verification`. We show authorization form once again.
 *      * `mfa_required`. We display additional login form.
 *      * `retrieving`. This means the user has logged in successfully. We add this PROVIDER_ACCOUNT to linked but we don't stop polling.
 *      * `gathering`. This means the user's data has been retrieved and we are processing it.
 *      * `updating`. The account is being updated during live update.
 *      * `failed_retrieval`. A backend couldn't fetch data from Yodlee for some reason. We mark an account as failed but display as linked.
 *      * `failed`. Something bad happen on a backend. We react in the same way as in case of `failed_retrieval`.
 *      * `ready`. All the data was fetched and we display this account as successfully added.
 */

type AuthenticationFormErrors = {
  credentials: Record<string, string[]>
}

const handleFormSubmitError = (store: Store<State>, params: AuthenticateLinkageRequestPayload, error: AjaxError): Observable<Action> => {
  // here we know this is an ajax error
  const state = store.getState()
  const linkageSession = linkageSessionSelector(state)

  if (!linkageSession) {
    captureWarning(`No active linkage session found`)
    return empty()
  }

  // we should be tolerant to [500, 600) errors and not show the error page
  if (isServerFailedError(error.status)) {
    // [500, 600) errors handling
    return merge(
      getCreateFakeProviderAccount$(store, linkageSession.cid).action$,
      of(authenticateLinkageActionCreator.fail(params)),
    )
  }

  if (error.status !== VALIDATION_ERROR_STATUS_CODE) {
    return processError(error)
  }

  const { response } = error.xhr

  const { cid } = linkageSession
  const linkageForm = linkageFormSelector(state, { cid })
  if (!linkageForm) {
    captureWarning(`No form found for an active linkage session`)
    return empty()
  }

  const mangledFieldNames = getMangledFieldNames(linkageForm.fields)

  const responseErrors: AuthenticationFormErrors = typeof response === 'string' ? JSON.parse(response) : response
  if (!response || !response.credentials) {
    captureWarning(`No valid provider credentials error`, { response })
    return empty()
  }

  const mangledFieldErrors = reduce<string[], Record<string, string[]>>(
    responseErrors.credentials,
    ((result, value, key) => ({ ...result, [mangledFieldNames[key]]: value })),
    {},
  )
  return from([
    stopSubmit(FORM_LINKAGE_AUTHENTICATION, getResponseFormErrors(mangledFieldErrors)),
  ])
}

/**
 * We create a fake provider accounts when initial provider request responds with 542 error
 * because we don't have any `providerAccountId` at this stage.
 *
 * But we need a provider account added to the stack on the page to show the `connection error` message.
 * Thus the `status` is `failed`.
 */
let fakeProviderAccountIndex = -1
const providerSelector = createProviderSelector()
// TODO: test
type GetCreateFakeProviderAccount$ReturnType = { action$: Observable<Action>, cid: MixedCompositeId | null }
const getCreateFakeProviderAccount$ = (store: Store<State>, cid: MixedCompositeId): GetCreateFakeProviderAccount$ReturnType => {
  const state = store.getState()
  const entityTypeFromCid = extractLinkageEntityFromCid(cid)
  const emptyResult = {
    action$: empty() as Observable<Action>,
    cid: null,
  }
  if (!entityTypeFromCid) {
    return emptyResult
  }

  if (entityTypeFromCid.type !== 'provider') {
    // we process only the case when no provider account associated with this `cid` has been created earlier
    return emptyResult
  }

  const provider = providerSelector(state, { cid })

  if (!provider) {
    captureWarning(`No provider found for fake provider account creation`, {
      cid,
    })
    return emptyResult
  }

  const fakeProviderId = --fakeProviderAccountIndex

  const fakeProviderAccount: ProviderAccount = {
    providerCid: provider.cid,
    is_manual: false,
    providerBalanceAccountCids: [],
    id: fakeProviderId,
    cid: createCompositeId('provider-account', fakeProviderId),
    meta: { replaced_id: null },
    // progress: null,
    created_at: moment().format(),
  }

  const action$ = of(createFakeProviderAccountActionCreator(fakeProviderAccount))

  return {
    action$,
    cid: fakeProviderAccount.cid,
  }
}

const searchProvidersEpic: Epic = (action$, _store, { get }) =>
  action$
    .pipe(filterRequestOfType(SEARCH_PROVIDERS, 'request')).pipe(
      switchMap((action) => {
        const { params } = action.payload
        return of(null).pipe(
          delay(300),
          mergeMap(() => get(GET_PROVIDERS_URL, { name: encodeURIComponent(params.query) })),
          takeUntil(
            action$
              .pipe(byType(CLEAR_PROVIDERS_SEARCH))
          ))
          .pipe(mapSafe(
            ({ response }) => {
              const providerCollection: ProviderCollection =
              normalize(
                response.map((provider): Provider => ({
                  ...provider,
                  cid: createCompositeId('provider', provider.id),
                })),
                'cid',
              )

              return searchProvidersActionCreator.success(params, providerCollection)
            },
          ))
      }))

const getProvidersEpic: Epic = (action$, _store, { get }) => {
  const getProviders$ = action$
    .pipe(filterRequestOfType(GET_PROVIDERS, 'request'))

  const providerSearch$ = getProviders$.pipe(
    filter((a) => a.payload.params.mode === 'search'))

  return getProviders$.pipe(
    mergeMap((action) => {
      const { params } = action.payload

      const endpointPayload: GetProvidersApiPayload = params.mode === 'popular'
        ? { popular: 1 }
        : { name: encodeURIComponent(params.query) }

      let request$ = get(GET_PROVIDERS_URL, endpointPayload)
      // cancel previous requests if new search action came
      if (params.mode === 'search') {
        request$ = request$.pipe(takeUntil(providerSearch$))
      }

      return request$
        .pipe(mapSafe(
          ({ response }) => {
            const providerCollection: ProviderCollection =
              normalize(
                response.map((provider): Provider => ({
                  ...provider,
                  cid: createCompositeId('provider', provider.id),
                })),
                'cid',
              )

            return getProvidersActionCreator.success(params, providerCollection)
          },
        ))
    }))
}

// TODO: test
const MFA_EXPIRATION_BUFFER = 100 // ms
export const processRawProviderAccount = (rawProviderAccount: Accomplishmentable<RawProviderAccount>, meta: MetaData, now: number = Date.now()): ProcessedRawProviderAccount => {
  const {
    provider: rawProvider,
    accounts: rawProviderBalanceAccounts,
    state: rawProviderAccountState,
    mfa_form: rawForm,
    accomplishment,
    ...rest
  } = rawProviderAccount

  const providerCid = createCompositeId('provider', rawProvider.id)
  const providerAccountCid = createCompositeId('provider-account', rawProviderAccount.id)

  const provider: Provider = {
    ...rawProvider,
    cid: providerCid,
  }

  const providerBalanceAccountCids: MixedCompositeId[] = []
  const providerBalanceAccounts = (rawProviderBalanceAccounts || []).map((rawProviderBalanceAccount): ProviderBalanceAccount => {
    const cid = createCompositeId('provider-balance-account', rawProviderBalanceAccount.id)
    providerBalanceAccountCids.push(cid)

    return {
      ...rawProviderBalanceAccount,
      cid,
      providerCid: providerCid,
      providerAccountCid,
      kind: getAccountKind(rawProviderBalanceAccount.category, meta),
    }
  })

  const providerAccount: ProviderAccount = {
    ...rest,
    cid: providerAccountCid,
    providerCid: providerCid,
    providerBalanceAccountCids,
  }

  let form: LinkageForm | undefined = undefined
  if (rawForm) {
    const { timeout, cid = providerAccountCid, ...restRawForm } = rawForm
    const partialForm = {
      ...restRawForm,
      cid,
    }
    if (timeout) {
      // transform seconds into milliseconds
      const msTimeout = Number(timeout) * 1000

      form = {
        ...partialForm,
        expiresAt: now + msTimeout + MFA_EXPIRATION_BUFFER,
      }
    }
  }

  return {
    provider,
    providerAccount,
    providerAccountState: {
      ...rawProviderAccountState,
      cid: providerAccountCid,
    },
    providerBalanceAccounts: normalize(providerBalanceAccounts, 'cid'),
    form,
  }
}

const processRawManualBalanceAccount = (rawManualBalanceAccount: RawManualBalanceAccount, meta: MetaData): ManualBalanceAccount => ({
  ...rawManualBalanceAccount,
  cid: createCompositeId('manual-balance-account', rawManualBalanceAccount.id),
  kind: getAccountKind(rawManualBalanceAccount.category, meta),
})

export const constructGetMixedAccountsSuccessPayload = (rawMixedAccounts: RawMixedProviderAccount[], meta: MetaData): GetMixedAccountsSuccessPayload => {
  const allProviders: Provider[] = []
  const allProviderAccounts: ProviderAccount[] = []
  const allProviderAccountStates: ProviderAccountState[] = []
  let allProviderBalanceAccounts: ProviderBalanceAccountCollection = createEmptyCollection()
  const allManualBalanceAccounts: ManualBalanceAccount[] = []
  const allForms: LinkageForm[] = []

  rawMixedAccounts.forEach((rawMixedAccount) => {
    if (!rawMixedAccount.is_manual) {
      const {
        provider,
        providerAccount,
        providerAccountState,
        providerBalanceAccounts,
        form,
      } = processRawProviderAccount(rawMixedAccount, meta)

      allProviders.push(provider)
      allProviderAccounts.push(providerAccount)
      allProviderAccountStates.push(providerAccountState)
      allProviderBalanceAccounts = concatCollections<ProviderBalanceAccount, 'cid'>(allProviderBalanceAccounts, providerBalanceAccounts)
      if (form) {
        allForms.push(form)
      }
    } else {
      const manualBalanceAccounts = rawMixedAccount.accounts.map(
        (account) => processRawManualBalanceAccount(account, meta),
      )

      allManualBalanceAccounts.push(...manualBalanceAccounts)
    }
  })

  const successPayload: GetMixedAccountsSuccessPayload = {
    providers: normalize(allProviders, 'cid'),
    providerAccounts: normalize(allProviderAccounts, 'cid'),
    providerAccountStates: normalize(allProviderAccountStates, 'cid'),
    providerBalanceAccounts: allProviderBalanceAccounts,
    manualBalanceAccounts: normalize(allManualBalanceAccounts, 'cid'),
    forms: normalize(allForms, 'cid'),
  }

  return successPayload
}

const getMixedAccountsEpic: Epic = (action$, store, { get, scheduler }) =>
  action$
    .pipe(
      filterRequestOfType(GET_MIXED_ACCOUNTS, 'request'),
      mergeMap((action) => {
        return get(GET_PROVIDER_ACCOUNTS_ALL_URL).pipe(
          mergeMapSafe(({ response }) => {
            const meta = metaSelector(store.getState())
            const processedMixedAccounts = constructGetMixedAccountsSuccessPayload(response, meta)
            const { providerAccounts, providerAccountStates, forms } = processedMixedAccounts
            const pollActions$ = providerAccounts.allKeys.reduce<Observable<Action>>(
              (result, cid) => {
                const providerAccount = providerAccounts.byKey[cid]
                const providerAccountState = providerAccountStates.byKey[cid]
                const form = forms.byKey[cid]
                if (providerAccount.is_manual) {
                  return result
                }

                if (isInProgressState(providerAccountState)) {
                  return concat(
                    result,
                    of(
                      pollProviderAccountActionCreator.request(providerAccount),
                    )
                  )
                }

                if (isMFAState(providerAccountState)) {
                  return concat(
                    result,
                    createMfaTimeout$({
                      form,
                      providerAccount,
                      action$,
                      scheduler,
                    }),
                  )
                }

                return result
              },
              empty(),
            )

            const successAction = getMixedAccountsActionCreator.success(
              action.payload.params,
              processedMixedAccounts,
            )

            return concat(
              of(successAction),
              pollActions$,
            )
          })
        )
      }),
    )

let sessionLinkageIndex = 0
type GetProviderIdsResult = {
  id: ProviderId
  cid: MixedCompositeId
}
const getProviderIds = (state: State, cid: MixedCompositeId): GetProviderIdsResult | null => {
  const mixedEntity = anyMixedLinkageEntitySelector(state, { cid })
  if (isProvider(mixedEntity)) {
    return {
      id: mixedEntity.id,
      cid: mixedEntity.cid,
    }
  }

  if (!isProviderAccount(mixedEntity)) {
    return null
  }

  const providerEntityFromCid = extractLinkageEntityFromCid(mixedEntity.providerCid)
  if (!providerEntityFromCid || !providerEntityFromCid.id) {
    return null
  }

  return {
    id: providerEntityFromCid.id,
    cid: mixedEntity.providerCid,
  }
}

const providerAccountStateSelector = createProviderAccountStateSelector()
const providerAccountSelector = createProviderAccountSelector()

type CreateMfaTimeoutArg = {
  form: LinkageForm | undefined
  providerAccount: ProviderAccount
  action$: Observable<Action>
  scheduler: TestScheduler | undefined
}

const createMfaTimeout$ = (arg: CreateMfaTimeoutArg): Observable<Action> => {
  const { form, providerAccount, action$, scheduler } = arg

  if (!form || !isNumber(form.expiresAt)) {
    return empty()
  }

  const timeout = form.expiresAt - Date.now()
  if (timeout < 0) {
    return empty()
  }

  const cancelMfa$ = action$
    .pipe(byType(CANCEL_MFA)).pipe(
      filter((a) => a.payload.params.cid === providerAccount.cid))

  const deleteProviderAccount$ = action$
    .pipe(byType(PERFORM_MIXED_ACCOUNT_OPERATION)).pipe(
      filter((a) =>
        a.payload.params.operation === 'delete'
      && a.payload.params.account.cid === providerAccount.cid
      ))

  const replaceProviderAccount$ = action$
    .pipe(byType(POLL_PROVIDER_ACCOUNT)).pipe(
      filter((a) => {
        if (!isSuccessAction(a)) {
          return false
        }

        const { meta } = a.payload.response.providerAccount
        return !!meta && isNumber(meta.replaced_id) && meta.replaced_id === providerAccount.id
      }))

  const authenticateMfa$ = action$
    .pipe(byType(AUTHENTICATE_LINKAGE)).pipe(
      filter((a) => a.payload.params.session.cid === providerAccount.cid))

  const cancelPolling$ = merge(
    cancelMfa$,
    deleteProviderAccount$,
    replaceProviderAccount$,
    authenticateMfa$
  )

  return of(pollProviderAccountActionCreator.request(providerAccount)).pipe(
    delay(timeout, scheduler),
    takeUntil(cancelPolling$))
}


const startLinkageSessionEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(START_LINKAGE_SESSION, 'request'))
    .pipe(mergeMapSafe((action) => {
      const state = store.getState()

      const { params } = action.payload
      const { cid } = params
      const { success } = startLinkageSessionActionCreator

      const sessionId = ++sessionLinkageIndex
      const entityFromCid = extractLinkageEntityFromCid(cid)
      if (!entityFromCid) {
        return empty()
      }

      const resultActions: Action[] = [
        blur(FORM_PROVIDER_SEARCH, FORM_PROVIDER_SEARCH_FIELD, { value: '', label: '' } as ProviderSearchOption),
      ]
      const providerAccountState = providerAccountStateSelector(state, { cid })
      const isMfaRequired = entityFromCid.type === 'provider-account'
        && providerAccountState && providerAccountState.status === 'mfa_required'

      if (entityFromCid.type === 'manual-balance-account') {
        resultActions.push(
          success(params, { sessionId }),
        )
      } else if (isMfaRequired) {
        const linkageForm = linkageFormSelector(state, { cid })
        if (linkageForm) {
          resultActions.push(
            success(params, { sessionId }),
          )
        } else {
          // no form found, requesting one again, hoping that helps
          const providerAccount = providerAccountSelector(state, { cid })
          if (!providerAccount) {
            captureWarning('No linkage form found for a provider account in `mfa_required` state.', {
              cid,
            })
            return empty()
          }
          resultActions.push(
            pollProviderAccountActionCreator.request(providerAccount),
          )
        }
      } else {
        const providerIds = getProviderIds(state, cid)
        if (!providerIds) {
          captureWarning('No provider id found for getting a detailed provider', { source: 'startLinkageSessionEpic' })
          return empty()
        }

        const isFakeProviderAccount = isFakeProviderAccountCid(cid)
        const sessionCid = isFakeProviderAccount ? providerIds.cid : cid

        resultActions.push(
          success(params, { sessionId, loading: true }),
          getDetailedProviderActionCreator.request({
            providerId: providerIds.id,
            session: { sessionId, cid: sessionCid },
          }),
        )

        if (isFakeProviderAccount) {
          const providerAccount = providerAccountSelector(state, { cid })
          if (!providerAccount) {
            captureWarning(`No fake provider account found in state`, {
              cid,
            })
            return empty()
          }
          resultActions.push(
            performMixedAccountOperationActionCreator.request({
              account: providerAccount,
              operation: 'delete',
            }),
          )
        }
      }

      return from(resultActions)
    }))

const ACTIVATE_ANCHOR_OPTIONS: AnchorActivationOptions = {
  behavior: 'smooth',
  block: 'nearest',
  preventScrollWhenVisible: true,
}
const getDetailedProviderEpic: Epic = (action$, store, { get }) =>
  action$
    .pipe(filterRequestOfType(GET_DETAILED_PROVIDER, 'request')).pipe(
      mergeMap((action) => {
        const { providerId, session } = action.payload.params
        return get({ url: GET_PROVIDER_DETAILS_URL, params: { id: providerId }}).pipe(
          mergeMap(({ response }) => {
            const { login_form, ...providerRest } = response
            if (!login_form) {
              captureWarning('No `login_form` got in provider details', {
                providerId,
              })

              return empty()
            }

            return of(
              getDetailedProviderActionCreator.success(
                action.payload.params, {
                  form: login_form,
                  provider: {
                    ...providerRest,
                    cid: createCompositeId('provider', providerRest.id),
                  },
                },
              ),
            )
          }))
          .pipe(catchAjaxSafe(() => {
            const { action$: fakeProviderAction$, cid } = getCreateFakeProviderAccount$(store, session.cid)

            let activateAnchor$ = empty() as Observable<Action>
            if (cid) {
              const anchorToActivate: Anchor = {
                anchor: 'settings-accounts_account',
                id: cid,
              }

              activateAnchor$ = of<Action>(
                activateAnchorActionCreator(anchorToActivate, ACTIVATE_ANCHOR_OPTIONS)
              )
            }

            return merge<Action>(
              of(getDetailedProviderActionCreator.fail(action.payload.params)),
              fakeProviderAction$,
              activateAnchor$
            )
          }))
      }))

const authenticateLinkageEpic: Epic = (action$, store, { post }) =>
  action$
    .pipe(filterRequestOfType(AUTHENTICATE_LINKAGE, 'request')).pipe(
      mergeMap((action) => {
        const { session, formValues } = action.payload.params
        const state = store.getState()
        const provider = providerBySessionSelector(state)
        const providerAccount = providerAccountBySessionSelector(state)
        const providerAccountState = providerAccountStateBySessionSelector(state)
        const meta = metaSelector(state)

        if (!provider) {
          captureWarning(`No provider corresponding to linkage session found`)
          return empty()
        }

        const { cid } = session
        const linkageForm = linkageFormSelector(state, { cid })
        if (!linkageForm) {
          captureWarning(`No form found in authentication session`, {
            cid,
          })
          return empty()
        }
        const rsaPublicKey = linkageForm.rsa_public_key
        const credentials = map(formValues, (value, key) => ({ name: key, value }))

        const resultPromise = encryptCredentials(
          JSON.stringify(credentials),
          rsaPublicKey,
        )

        return from(resultPromise).pipe(
          mergeMap((encryptedCredentials) => {

            let request$: Observable<AjaxResponse<RawProviderAccount>> | null = null
            if (!providerAccount || !providerAccountState) {
            // need to create a provider account
              request$ = post(
                CREATE_PROVIDER_ACCOUNT_URL,
                { body: { credentials: encryptedCredentials, provider_id: provider.id }},
              )
            } else if (providerAccountState.status === 'mfa_required') {
            // need to set mfa credentials
              request$ = post(
                { url: SET_PROVIDER_ACCOUNT_MFA_URL, params: { id: providerAccount.id }},
                { body: { credentials: encryptedCredentials }},
              )
            } else {
            // need just to update existing provider account
              request$ = post(
                { url: UPDATE_PROVIDER_ACCOUNT_URL, params: { id: providerAccount.id }},
                { body: { credentials: encryptedCredentials }},
              )
            }

            return request$.pipe(
              mergeMap(({ response }) => {
                const processedRawProviderAccount = processRawProviderAccount(response, meta)

                const actions: Action[] = [
                  authenticateLinkageActionCreator.success(action.payload.params, processedRawProviderAccount),
                ]
                if (isInProgressState(processedRawProviderAccount.providerAccountState)) {
                  actions.push(
                    pollProviderAccountActionCreator.request(
                      processedRawProviderAccount.providerAccount,
                    ),
                  )
                }
                if (isFakeProviderAccountCid(session.cid) && providerAccount !== null) {
                  actions.push(
                    performMixedAccountOperationActionCreator.request({
                      account: providerAccount,
                      operation: 'delete',
                    }),
                  )
                }

                actions.push(
                  activateAnchorActionCreator(getProviderAccountAnchorId(processedRawProviderAccount.providerAccount.cid), {
                    behavior: 'smooth',
                    block: 'start',
                    preventScrollWhenVisible: true,
                  }),
                  pushActionCreator(LINKED_ACCOUNTS_URL)
                )

                return of(...actions)
              }))
              .pipe(catchAjaxSafe((error) => handleFormSubmitError(store, action.payload.params, error)))
          }),
          catchError((err) => {
            captureWarning('Something wrong with credentials encryption', err)
            return empty()
          }))
      }))

const cancelMfaEpic: Epic = (action$, store, { post }) =>
  action$
    .pipe(filterRequestOfType(CANCEL_MFA, 'request')).pipe(
      mergeMap((action) => {
        const request$ = post(
          { url: CANCEL_PROVIDER_ACCOUNT_MFA_URL, params: { id: action.payload.params.id }},
        )

        const { success, fail } = cancelMfaActionCreator

        return request$.pipe(
          mergeMap(({ response: rawProviderAccount }) => {
            const meta = metaSelector(store.getState())
            const processedRawProviderAccount = processRawProviderAccount(rawProviderAccount, meta)
            return of(success(action.payload.params, processedRawProviderAccount))
          }))
          .pipe(catchAjaxSafe(
            () => of(fail(action.payload.params)),
          ))
      }))

const fetchMissingAccountsEpic: EpicRefined<FetchMissingAccountsAction | AjaxCatchExtraActions> = (action$, _store, { post }) => action$.pipe(
  filterRequest(FetchMissingAccountsAction.type),
  mergeMap((_action) => {
    return merge(
      post(FETCH_MISSING_ACCOUNTS_URL, { body: {}}).pipe(
        mergeMap(() => EMPTY),
        ajaxCatch(() => EMPTY),
      ),
    )
  }),
)

const POLLING_DELAY = 1000

const pollProviderAccountEpic: Epic = (action$, store, { get, scheduler }) =>
  action$
    .pipe(filterRequestOfType(POLL_PROVIDER_ACCOUNT, 'request')).pipe(
      mergeMap((action) => {
        const state = store.getState()
        const meta = metaSelector(state)
        const { id, cid } = action.payload.params
        const prevProviderAccountState = providerAccountStateSelector(state, { cid })

        return get({ url: GET_PROVIDER_ACCOUNT_STATE_URL, params: { id }}).pipe(
          mergeMap(({ response: rawProviderAccount }) => {

            const processedRawProviderAccount = processRawProviderAccount(rawProviderAccount, meta)
            const { providerAccount, providerAccountState, form } = processedRawProviderAccount

            const success$ = of(
              pollProviderAccountActionCreator.success(
                action.payload.params,
                processedRawProviderAccount,
              ),
            )

            const setAccomplishment$ = rawProviderAccount.accomplishment
              ? of(addAccomplishmentActionCreator(rawProviderAccount.accomplishment))
              : empty()

            let mfaTimeoutProcessing$: Observable<Action> = empty()
            if (prevProviderAccountState && prevProviderAccountState.status === 'mfa_required') {
              if (providerAccountState.status !== 'failed_verification') {
                captureWarning(`Invalid provider account status after \`mfa_required\``, {
                  cid,
                  status: providerAccountState.status,
                })
              } else {
                const sessions = getItemsFromCollection<LinkageSession, 'sessionId'>(state.app.linkage.sessions)
                const mfaSessions = sessions.filter(({ cid: mixedCid }) => mixedCid === cid)
                if (mfaSessions.length > 1) {
                  captureWarning(`More than one linkage sessions found for \`mfa_required\` provider account`, {
                    cid,
                  })
                } else if (mfaSessions.length === 1) {
                  mfaTimeoutProcessing$ = of(
                    killLinkageSessionActionCreator(mfaSessions[0].sessionId),
                  )
                }
              }
            }

            const polling$ = isInProgressState(providerAccountState)
              ? of(pollProviderAccountActionCreator.request(action.payload.params)).pipe(delay(POLLING_DELAY),
                takeUntil(
                  action$.pipe(filter((a) =>
                    a.type === PERFORM_MIXED_ACCOUNT_OPERATION
                    && a.payload.params.operation === 'delete'
                    && isSuccessAction(a)
                    && a.payload.params.account.cid === cid,
                  )),
                ))
              : empty()


            let startSession$: Observable<Action> = empty()
            if (providerAccountState.status === 'mfa_required' && form) {
              startSession$ = concat(
                startSession$,
                of(
                  startLinkageSessionActionCreator.request(
                    { cid: action.payload.params.cid },
                  ),
                )
              )

              if (form.expiresAt) {
                startSession$ = concat(
                  startSession$,
                  createMfaTimeout$({
                    form,
                    providerAccount,
                    action$,
                    scheduler,
                  })
                )
              }
            }

            providerAccountState.status === 'mfa_required' && rawProviderAccount.mfa_form
              ? of(startLinkageSessionActionCreator.request(
                { cid: action.payload.params.cid },
              ))
              : empty()

            return merge(
              success$,
              setAccomplishment$,
              mfaTimeoutProcessing$,
              polling$,
              startSession$,
            )
          }))
          .pipe(catchAjaxSafe((error) => {
            if (error.status === NOT_FOUND_STATUS_CODE) {
              return of(
                performMixedAccountOperationActionCreator.request({
                  account: action.payload.params,
                  operation: 'delete',
                  noop: true,
                }),
              )
            }

            return processError(error)
          }))
      }))

const performMixedAccountOperationEpic: Epic = (action$, store, { post }) =>
  action$
    .pipe(filterRequestOfType(PERFORM_MIXED_ACCOUNT_OPERATION, 'request')).pipe(
      mergeMap((action) => {
        const { account, operation, noop } = action.payload.params

        const entityFromCid = extractLinkageEntityFromCid(account.cid)
        if (!entityFromCid) {
          return empty()
        }

        const { success, fail } = performMixedAccountOperationActionCreator

        let request$: Observable<AjaxResponse<MixedAccountOperationApiResponse | {}>> | null = null

        // TODO: test
        const success$ = of(success(action.payload.params, {}))
        if (noop) {
          return success$
        }

        const { id } = account
        switch (entityFromCid.type) {
          case 'provider-account':
            if (isFakeProviderAccountCid(account.cid)) {
              return success$
            }

            if (operation === 'delete') {
              request$ = post(
                { url: DELETE_PROVIDER_ACCOUNT_URL, params: { id }},
              )
            }
            break

          case 'provider-balance-account':
            if (operation === 'delete') {
              request$ = post(
                { url: DELETE_PROVIDER_BALANCE_ACCOUNT_URL, params: { id }},
              )
            }

            if (operation === 'link') {
              request$ = post(
                { url: LINK_PROVIDER_BALANCE_ACCOUNT_URL, params: { id }},
              )
            }

            if (operation === 'unlink') {
              request$ = post(
                { url: UNLINK_PROVIDER_BALANCE_ACCOUNT_URL, params: { id }},
              )
            }

            break

          case 'manual-balance-account':
            if (operation === 'delete') {
              request$ = post(
                { url: DELETE_MANUAL_BALANCE_ACCOUNT_URL, params: { id }},
              )
            }

            if (operation === 'unlink') {
              request$ = post(
                { url: UNLINK_MANUAL_BALANCE_ACCOUNT_URL, params: { id }},
              )
            }
            break

          default:
            return empty()
        }

        if (!request$) {
          captureWarning(`No appropriate operation url found`, {
            source: 'performMixedAccountOperationEpic',
            cid: account.cid,
            operation,
          })
          return empty()
        }

        return request$.pipe(
          mergeMap(({ response }) => {
            return concat(success$, processApiActions(response, store.getState()))
          }))
          .pipe(catchAjaxSafe((error) => of(fail(action.payload.params, error.status))))
      }))


const manualAccountEditedEpic: Epic = (action$, store) => {
  return action$
    .pipe(formSubmitted(FORM_MANUAL_ACCOUNT)).pipe(
      mergeMap((action) => {
      // needed formSubmitted method cannot resolve types
        if (action.payload.formName !== FORM_MANUAL_ACCOUNT) {
          return empty()
        }

        const state = store.getState()
        const meta = metaSelector(state)
        const session = linkageSessionSelector(state)
        if (!session) {
          captureWarning('Manual account form submitted, but no linkage session found')
          return empty()
        }
        const manualBalanceAccount = processRawManualBalanceAccount(action.payload.response, meta)

        const resultActions: Action[] = [
          setManualBalanceAccountActionCreator(manualBalanceAccount),
          killLinkageSessionActionCreator(session.sessionId),
        ]

        const requestedSessionCid = state.app.linkage.requestedSession
        if (requestedSessionCid !== null) {
          resultActions.push(
            startLinkageSessionActionCreator.request({ cid: requestedSessionCid })
          )
        } else {
        // move to freshly created account only if a user clicked `finish` not `add another account`.
          resultActions.push(
            activateAnchorActionCreator(getProviderAccountAnchorId(manualBalanceAccount.cid), {
              behavior: 'smooth',
              block: 'start',
              preventScrollWhenVisible: true,
            }),
            pushActionCreator(LINKED_ACCOUNTS_URL),
            killAllFloatingPanesActionCreator(),
          )
        }

        return from(resultActions)
      }))
}

const updateMemberEpic: Epic = (action$) =>
  action$.pipe(
    filter((a) =>
      a.type === SET_MANUAL_BALANCE_ACCOUNT
      || isAccountOperationSucceed(a)
      || isPollingProviderAccountReady(a),
    ),
    throttleTime(100),
    mapTo(markDataStaleActionCreator('member')))


const handleCTAEpic: Epic = (action$) =>
  action$
    .pipe(byType(INSIGHT_CTA_CLICK)).pipe(
      filter(({ payload }) => !!(payload.type === 'add_account')),
      mergeMap(() =>
        of(modifyFloatingPaneActionCreator('provider-search', {}))
      ))

const clearRecentAddedEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(CLEAR_RECENTLY_ADDED, 'request')).pipe(
      mergeMap(({ payload }) => {
        const state = store.getState()
        const states = providerAccountStatesSelector(state)
        if (!states) {
          return empty()
        }

        const providerAccountCids = states.allKeys
          .map((key) => states.byKey[key])
          .filter((accountState) => !isInProgressState(accountState))
          .map((accountState) => accountState.cid)

        return of(clearRecentlyAddedActionCreator.success(payload, { providerAccountCids }))
      }))

export default combineEpics(
  searchProvidersEpic,
  getProvidersEpic,
  getMixedAccountsEpic,
  startLinkageSessionEpic,
  getDetailedProviderEpic,
  authenticateLinkageEpic,
  pollProviderAccountEpic,
  performMixedAccountOperationEpic,
  cancelMfaEpic,
  fetchMissingAccountsEpic,
  manualAccountEditedEpic,
  updateMemberEpic,
  handleCTAEpic,
  clearRecentAddedEpic,
)
